import React, { useMemo } from 'react';

import { useMediaQuery } from 'react-responsive';
import Loading from 'components/layout/loading';
import moment from 'moment';
import BaseSelected from '../../RefuelContainer/components/BaseSelected';
import RefuelStatus from './RefuelStatus';

import localImg from '../../assets/local.svg';
import gasImg from '../../assets/gasIcon.svg';
import calendarImg from '../../assets/calendar.svg';

import { useNewRefuel } from '../../hooks/useNewRefuel';

import {
  Container,
  SelectBasesWrap,
  ConfirmDataWrap,
  BasesContainer,
  ConfirmDataHeader,
  TitleWrapper,
  Title,
  InfoCard,
  InfoCardLabel,
  InfoValue,
  DataCard,
  StatusWrapper,
  StatusIcon,
  StatusText,
  ProtocolWrapper,
  ProtocolLabel,
  ProtocolValue,
  CardHeaderWrapper,
  RefuelDateLabel,
  RefuelDateWrapper,
  DataCardOneWrapper,
  RefuelStatusWrapper,
  InfoEmailWrapper,
  InfoEmailText,
  InfoEmailTextStrong,
  CardResumeWrapper,
  RefualDateValue,
  CardResumeItems,
  CardResumeItemWrapper,
  CardResumeIcon,
  CardResumeTitle,
  CardResumeInfo,
  CardResumeInfosWrapper,
  Divider,
  ImgIcon,
  CardResumeTitleWrapper,
  CardResumeTitleHeader,
} from './styles';

const ConfirmRefuel: React.FC = () => {
  const { selectedBases, selectedBase, refuelData, isLoading, statusResponse } =
    useNewRefuel();

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const status = useMemo(() => {
    const [res] = statusResponse.filter(
      (stsResponse) => stsResponse.baseName === selectedBase?.name
    );
    return { ...res };
  }, [selectedBase, statusResponse]);

  const refuelInfo = useMemo(() => {
    const [base] = selectedBases.filter(
      (baseFind) => baseFind.name === selectedBase?.name
    );
    const [refuelResponse] = refuelData.filter(
      (refuel) => refuel.baseName === selectedBase?.name
    );

    return {
      base,
      refuelResponse,
    };
  }, [selectedBase, refuelData, selectedBases]);

  const refuelDateFormatted = useMemo(() => {
    return new Date(refuelInfo.refuelResponse.refuelDate).toLocaleDateString(
      'pt-BR',
      {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }
    );
  }, [refuelInfo]);

  return (
    <>
      {isLoading && <Loading />}
      <Container>
        <SelectBasesWrap>
          <BasesContainer>
            {!isMobile && selectedBases.length > 0 && (
              <>
                {selectedBases.map((base) => (
                  <BaseSelected
                    isComplete
                    isSelected={base.name === selectedBase?.name}
                    baseName={base.name}
                    nickName={base.nickname}
                    address={`${base.address}, ${base.number} - ${base.complement}`}
                  />
                ))}
              </>
            )}
          </BasesContainer>
        </SelectBasesWrap>
        <ConfirmDataWrap>
          {!isMobile && (
            <ConfirmDataHeader>
              <TitleWrapper>
                <Title>Confirmação do pedido</Title>
                <InfoCard>
                  <InfoCardLabel>N do pedido</InfoCardLabel>
                  <InfoValue>Em processamento</InfoValue>
                </InfoCard>
              </TitleWrapper>
              {status?.status !== 'error' && (
                <RefuelDateWrapper>
                  <RefuelDateLabel>Pedido feito em</RefuelDateLabel>
                  {status?.createdDate && (
                    <RefualDateValue>
                      {moment(status.createdDate).format(
                        'DD/MM/YYYY [às] HH:mm'
                      )}
                    </RefualDateValue>
                  )}
                </RefuelDateWrapper>
              )}
            </ConfirmDataHeader>
          )}
          {isMobile && (
            <ConfirmDataHeader>
              <TitleWrapper>
                <Title>Confirmação</Title>
                {status?.status !== 'error' && (
                  <RefuelDateWrapper>
                    <RefuelDateLabel>Pedido feito em</RefuelDateLabel>
                    <RefualDateValue>18/10/2024 às 13:37</RefualDateValue>
                  </RefuelDateWrapper>
                )}
                <InfoCard>
                  <InfoCardLabel>N do pedido</InfoCardLabel>
                  <InfoValue>Em processamento</InfoValue>
                </InfoCard>
              </TitleWrapper>
            </ConfirmDataHeader>
          )}
          <DataCardOneWrapper>
            <DataCard>
              <CardHeaderWrapper>
                <StatusWrapper>
                  <StatusIcon isError={status?.status === 'error'} />
                  <StatusText isError={status?.status === 'error'}>
                    {status?.status === 'error' && status.message
                      ? status.message
                      : 'Seu pedido está em análise'}
                  </StatusText>
                </StatusWrapper>
                <ProtocolWrapper>
                  <ProtocolLabel>N do protocolo</ProtocolLabel>
                  <ProtocolValue>
                    {status?.status === 'error'
                      ? '-'
                      : status?.caseNumber || 'Processando...'}
                  </ProtocolValue>
                </ProtocolWrapper>
              </CardHeaderWrapper>
              <RefuelStatusWrapper>
                <RefuelStatus />
              </RefuelStatusWrapper>
              {!refuelInfo.refuelResponse.isFixedRoute &&
                status.status === 'warning' && (
                  <InfoEmailWrapper
                    dangerouslySetInnerHTML={{ __html: status.message || '' }}
                  />
                )}
            </DataCard>
            <CardResumeWrapper>
              <DataCard>
                <CardResumeTitleWrapper>
                  <CardResumeTitleHeader>
                    Resumo do pedido
                  </CardResumeTitleHeader>
                </CardResumeTitleWrapper>
                <CardResumeItems>
                  <CardResumeItemWrapper>
                    <CardResumeIcon>
                      <img src={localImg} alt="Local" />
                    </CardResumeIcon>
                    <CardResumeInfosWrapper>
                      <CardResumeTitle>
                        {refuelInfo.base.nickname}
                      </CardResumeTitle>
                      <CardResumeInfo>{`${refuelInfo.base.address}, ${refuelInfo.base.number} - ${refuelInfo.base.complement}`}</CardResumeInfo>
                    </CardResumeInfosWrapper>
                  </CardResumeItemWrapper>
                  <Divider />
                  <CardResumeItemWrapper>
                    <CardResumeIcon>
                      <img src={gasImg} alt="Nível de gás" />
                    </CardResumeIcon>
                    <CardResumeInfosWrapper>
                      <CardResumeTitle>Nível atual do gás (%)</CardResumeTitle>
                      <CardResumeInfo>{`${refuelInfo.refuelResponse.currentPercentage}% do gás`}</CardResumeInfo>
                    </CardResumeInfosWrapper>
                  </CardResumeItemWrapper>
                  <Divider />
                  <CardResumeItemWrapper>
                    <CardResumeIcon>
                      <ImgIcon src={calendarImg} alt="Previsão de entrega" />
                    </CardResumeIcon>
                    <CardResumeInfosWrapper>
                      <CardResumeTitle>Previsão de entrega</CardResumeTitle>
                      <CardResumeInfo>{refuelDateFormatted}</CardResumeInfo>
                    </CardResumeInfosWrapper>
                  </CardResumeItemWrapper>
                </CardResumeItems>
              </DataCard>
            </CardResumeWrapper>
          </DataCardOneWrapper>
        </ConfirmDataWrap>
      </Container>
    </>
  );
};

export default ConfirmRefuel;
